import { useEffect, useState, ReactNode } from 'react';

interface ModalOverlayProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** numeric z-index for the backdrop layer; content will be z+1 */
  zIndex?: number;
}

export default function ModalOverlay({ open, onClose, children, zIndex = 50 }: ModalOverlayProps) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(raf);
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!mounted) return null;

  return (
    <>
      {/* Layer A — backdrop, handles click-outside-to-close */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex,
          background: 'rgba(15,23,42,0.35)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          transition: 'opacity 0.28s ease',
          opacity: visible ? 1 : 0,
        }}
        onClick={onClose}
      />

      {/* Layer B — content, always 1 above backdrop so it never receives backdrop clicks */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: zIndex + 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            pointerEvents: 'auto',
            transition: 'opacity 0.28s cubic-bezier(0.22,1,0.36,1), transform 0.28s cubic-bezier(0.22,1,0.36,1)',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.97)',
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
}
